import React, { useEffect, useState } from 'react';
import { AgentRealtimeSection } from '../components/dashboard/AgentRealtimeSection';
import { AgentUpdatesSection } from '../components/dashboard/AgentUpdatesSection';
import { DashboardHeroHeading } from '../components/dashboard/DashboardHeroHeading';
import { DashboardKpiRow } from '../components/dashboard/DashboardKpiRow';
import { SystemHealthSection } from '../components/dashboard/SystemHealthSection';
import { dashboardService } from '../services/dashboardService';
import { buildBusinessSnapshot, buildKpis } from '../lib/dashboard/mapOverview';
import { DashboardOverview } from '../types/dashboard';
import { Container, Page } from '../components/layout/primitives';
import dashboardTexture from '../assets/dashboard/image 8.png';

export const ModernDashboard: React.FC = () => {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await dashboardService.overview(30);
        if (!cancelled) setOverview(data);
      } catch (e) {
        console.error('Failed to load dashboard overview:', e);
        if (!cancelled) setError('Could not load your dashboard data right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Page containerSize="full" contentClassName="px-0 py-0 pb-6">
      <section className="relative left-1/2 isolate w-screen max-w-none -translate-x-1/2 overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 z-0 w-screen -translate-x-1/2">
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgb(var(--c-f5f7fa,245_247_250))_0%,rgb(var(--c-f5f7fa,245_247_250))_72%,rgb(var(--c-f3eefb,243_238_251))_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgb(var(--c-f5f7fa,245_247_250)/0)_58%,rgb(var(--c-edf5f7,237_245_247)/0.95)_100%)]" />
          <div className="absolute -right-[31.2%] -top-[320px] h-[1001px] w-[1001px] rounded-full bg-[radial-gradient(circle,rgb(var(--c-d6e3e8,214_227_232)/0.75)_0%,rgb(var(--c-d6e3e8,214_227_232)/0)_72%)]" />
          <div className="absolute left-[5.5%] top-[74%] h-[348px] w-[971px] -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgb(var(--c-dbd2ec,219_210_236)/0.52)_0%,rgb(var(--c-dbd2ec,219_210_236)/0)_74%)]" />
        </div>
        <Container size="wide" className="relative z-10 max-w-[1466px] pb-10 pt-8 sm:pb-12 sm:pt-10 md:pt-14">
          <DashboardHeroHeading />
          {loading ? (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:mx-auto xl:max-w-[1466px] xl:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="min-h-[224px] animate-pulse rounded-[20px] bg-white/60 sm:min-h-[256px] lg:min-h-[300px]" />
              ))}
            </div>
          ) : error || !overview ? (
            <p className="text-center text-sm text-red-500">{error ?? 'No dashboard data available.'}</p>
          ) : (
            <DashboardKpiRow items={buildKpis(overview)} />
          )}
        </Container>
      </section>

      {!loading && overview && (
        <>
          <section className="relative left-1/2 isolate w-screen max-w-none -translate-x-1/2 overflow-hidden bg-c-f6f8fc py-10 sm:py-12 md:py-14 lg:py-16">
            <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
              <img src={dashboardTexture} alt="" className="absolute inset-0 h-full w-full object-cover opacity-10" />
              <div className="absolute inset-0 bg-white/72" />
              <div className="absolute -left-[13.8%] top-[40%] h-[600px] w-[600px] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgb(var(--c-d5cdeb,213_205_235)/0.55)_0%,rgb(var(--c-d5cdeb,213_205_235)/0)_72%)]" />
              <div className="absolute right-[-25%] top-[66%] h-[969px] w-[969px] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgb(var(--c-f2e2eb,242_226_235)/0.42)_0%,rgb(var(--c-f2e2eb,242_226_235)/0)_72%)]" />
            </div>
            <Container size="wide" className="relative z-10 max-w-[1466px]">
              <AgentRealtimeSection overview={overview} />
            </Container>
          </section>

          <section className="relative left-1/2 isolate w-screen max-w-none -translate-x-1/2 overflow-hidden bg-c-f8f8fc py-10 sm:py-12 md:py-14 lg:py-16">
            <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
              <div className="absolute right-[-28%] top-[52%] h-[969px] w-[969px] -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgb(var(--c-efdde6,239_221_230)/0.55)_0%,rgb(var(--c-efdde6,239_221_230)/0)_74%)]" />
              <div className="absolute -left-[8%] top-[36%] h-[300px] w-[300px] rounded-[999px] bg-[radial-gradient(circle,rgb(var(--c-bedff1,190_223_241)/0.23)_0%,rgb(var(--c-bedff1,190_223_241)/0)_76%)]" />
              <div className="absolute -left-[4%] top-[56%] h-[300px] w-[300px] rounded-[999px] bg-[radial-gradient(circle,rgb(var(--c-bedff1,190_223_241)/0.23)_0%,rgb(var(--c-bedff1,190_223_241)/0)_76%)]" />
              <div className="absolute -left-[8%] top-[76%] h-[300px] w-[300px] rounded-[999px] bg-[radial-gradient(circle,rgb(var(--c-bedff1,190_223_241)/0.23)_0%,rgb(var(--c-bedff1,190_223_241)/0)_76%)]" />
            </div>
            <Container size="wide" className="relative z-10 max-w-[1360px]">
              <AgentUpdatesSection
                topAgents={overview.topAgents}
                periodConversations={overview.kpis.conversationsHandled.value}
                periodDays={overview.periodDays}
              />
            </Container>
          </section>

          <section className="relative left-1/2 w-screen max-w-none -translate-x-1/2 bg-c-f3f4fa py-8 sm:py-10 md:py-12">
            <Container size="wide" className="max-w-[1466px]">
              <SystemHealthSection metrics={buildBusinessSnapshot(overview)} />
            </Container>
          </section>
        </>
      )}
    </Page>
  );
};
